import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService } from '../../services';
import { getUserFriendlyErrorMessage } from '../services/error-handling';
import { logError, getErrorMessageString } from '../utils/error-handler';
import { Subscription } from '../types/index';
import teamSessionService from '../../services/TeamSessionService';
import { TeamSessionLimitDialog } from '../components/common/dialogs/TeamSessionLimitDialog';
import { SubscriptionExpiredDialog } from '../components/common/dialogs/SubscriptionExpiredDialog';
import { useUserOrganizations, useUserTeams } from '../hooks/queries';
import { queryKeys } from '../config/react-query.config';

export interface Organization {
  id: string;
  _id?: string; // Optional for compatibility with API responses
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  createdOn?: string; // Alternative field name from some API responses
  updatedOn?: string; // Alternative field name from some API responses
  isImplicit?: boolean; // True if user only has implicit access through team membership
  role?: string; // User's role in this organization (e.g., 'owner', 'admin', 'member')
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  organizationId: string;
  organizationName?: string;
  isPublic?: boolean;
  createdAt: string;
  updatedAt: string;
  role?: string; // User's role in this team (e.g., 'admin', 'member')
}

export interface Member {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  avatarUrl?: string;
  status: 'active' | 'invited' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

export interface Invitation {
  id: string;
  email: string;
  userId?: string;
  scope: 'organization' | 'team';
  contextId: string;
  organizationId: string;
  roleId?: string;
  invitedBy: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired' | 'canceled' | 0 | 1 | 2 | 3 | 4;
  expiresAt: string;
  token: string;
  createdAt: string;
  updatedAt: string;
  organizationName?: string;
  teamName?: string;
  roleName?: string;
}


interface OrganizationContextType {
  organizations: Organization[];
  teams: Team[];
  members: Record<string, Member[]>; // Keyed by organizationId
  invitations: Invitation[]; // User's pending invitations
  organizationInvitations: Record<string, Invitation[]>; // Keyed by organizationId
  subscriptions: Record<string, Subscription>;
  currentOrganizationId: string | null;
  currentTeamId: string | null;
  currentSubscriptionId: string | null;
  setCurrentOrganizationId: (id: string | null, navigate?: boolean) => void;
  setCurrentTeamId: (id: string | null, navigate?: boolean) => void;
  setCurrentSubscriptionId: (id: string | null) => void;
  syncWithCurrentRoute: (orgId?: string, teamId?: string) => void;
  getOrganizationSubscriptions: (organizationId: string) => Subscription[];
  getOrganizationMembers: (organizationId: string) => Member[];
  getOrganizationInvitations: (organizationId: string) => Invitation[];
  assignSubscriptionToTeam: (subscriptionId: string, teamId: string) => Promise<void>;
  unassignSubscriptionFromTeam: (subscriptionId: string, teamId: string) => Promise<void>;
  getTeamSubscription: (teamId: string) => Subscription | null;
  addSubscription: (subscription: Subscription) => void;
  createNewSubscription: (data: { name: string; planId: string; organizationId: string }) => Promise<Subscription>;
  cancelSubscription: (id: string) => Promise<Subscription>;
  updateSubscriptionAddOns: (id: string, addOns: { extraUsers?: number; extraStorage?: number; extraTeams?: number; extraApiCalls?: number }) => Promise<Subscription>;
  updateBillingDetails: (subscriptionId: string, billingDetails: { paymentMethod: string; cardNumber?: string; expiryDate?: string; cvc?: string; billingAddress: string; }) => Promise<Subscription>;
  getBillingHistory: (subscriptionId: string) => Promise<any[]>;
  setupStripeCustomer: (organizationId: string, data: { email: string, name: string, billingAddress: string, taxId?: string }) => Promise<{ customerId: string }>;
  createStripePaymentMethod: (organizationId: string, data: { customerId: string, paymentMethodId: string }) => Promise<{ success: boolean }>;
  getStripeSetupIntent: (organizationId: string) => Promise<{ clientSecret: string }>;
  updateSubscriptionAutoRenew: (subscriptionId: string, autoRenew: boolean) => Promise<Subscription>;
  downloadInvoice: (invoiceId: string) => Promise<{ downloadUrl: string }>;
  createOrganization: (data: { name: string; description?: string }) => Promise<Organization>;
  updateOrganization: (id: string, data: { name?: string; description?: string }) => Promise<Organization>;
  deleteOrganization: (id: string) => Promise<void>;
  createTeam: (data: { name: string; description?: string; organizationId: string; subscriptionId?: string }) => Promise<Team>;
  updateTeam: (id: string, data: { name?: string; description?: string }) => Promise<Team>;
  deleteTeam: (id: string) => Promise<void>;
  inviteMember: (organizationId: string, data: { email: string; role: string }) => Promise<any>;
  acceptInvitation: (invitationId: string) => Promise<void>;
  declineInvitation: (invitationId: string) => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  removeMember: (organizationId: string, userId: string) => Promise<void>;
  updateMemberRole: (organizationId: string, userId: string, role: string) => Promise<Member>;
  refreshData: () => Promise<void>;
  refreshInvitations: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};

// Persistence utilities
const STORAGE_KEYS = {
  CURRENT_ORGANIZATION_ID: 'sharp_current_organization_id',
  CURRENT_TEAM_ID: 'sharp_current_team_id',
  USER_SELECTED_ORG: 'sharp_user_selected_org'
};

const persistToLocalStorage = (key: string, value: any) => {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.warn(`Failed to save ${key} to localStorage:`, error);
  }
};

const loadFromLocalStorage = (key: string, defaultValue: any = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`Failed to load ${key} from localStorage:`, error);
    return defaultValue;
  }
};

export const OrganizationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { showError, showWarning } = useNotification();
  const queryClient = useQueryClient();

  // Use React Query for organizations and teams (automatic deduplication!)
  const { data: directOrganizations = [], isLoading: orgsLoading } = useUserOrganizations();
  const { data: teams = [], isLoading: teamsLoading } = useUserTeams();

  // Combine direct organizations with implicit organizations (orgs where user only has team membership)
  const [implicitOrganizations, setImplicitOrganizations] = useState<Organization[]>([]);

  // Computed organizations array that includes both direct and implicit orgs
  const organizations = React.useMemo(() => {
    return [...directOrganizations, ...implicitOrganizations];
  }, [directOrganizations, implicitOrganizations]);

  // Keep local state for members (not yet migrated to React Query)
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [organizationInvitations, setOrganizationInvitations] = useState<Record<string, Invitation[]>>({});
  const [subscriptions, setSubscriptions] = useState<Record<string, Subscription>>({});
  // Initialize state from localStorage
  const [currentOrganizationId, setCurrentOrganizationIdState] = useState<string | null>(() => 
    loadFromLocalStorage(STORAGE_KEYS.CURRENT_ORGANIZATION_ID)
  );
  const [currentTeamId, setCurrentTeamIdState] = useState<string | null>(() => 
    loadFromLocalStorage(STORAGE_KEYS.CURRENT_TEAM_ID)
  );
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState<string | null>(null);
  const [additionalDataLoading, setAdditionalDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute combined loading state from React Query + additional data
  const isLoading = orgsLoading || teamsLoading || additionalDataLoading;
  const [userSelectedOrg, setUserSelectedOrg] = useState<boolean>(() =>
    loadFromLocalStorage(STORAGE_KEYS.USER_SELECTED_ORG, false)
  );
  // Team session limit dialog state
  const [sessionLimitDialogOpen, setSessionLimitDialogOpen] = useState(false);
  const [sessionLimitInfo, setSessionLimitInfo] = useState<{ currentUsers: number; maxUsers: number }>({ currentUsers: 0, maxUsers: 0 });
  const [subscriptionExpiredDialogOpen, setSubscriptionExpiredDialogOpen] = useState(false);
  // Prevent concurrent refreshData calls using ref to avoid re-renders
  const refreshDataInProgressRef = React.useRef(false);

  // Empty placeholders for organization data
  const dummyOrganizations: Organization[] = [];
  const dummyTeams: Team[] = [];
  const dummySubscriptions: Record<string, Subscription> = {};

  // Internal helpers to update state and persist to localStorage
  const updateCurrentOrganizationId = useCallback((id: string | null) => {
    setCurrentOrganizationIdState(id);
    persistToLocalStorage(STORAGE_KEYS.CURRENT_ORGANIZATION_ID, id);
  }, []);

  const updateCurrentTeamId = useCallback((id: string | null) => {
    setCurrentTeamIdState(id);
    persistToLocalStorage(STORAGE_KEYS.CURRENT_TEAM_ID, id);
  }, []);

  const updateUserSelectedOrg = useCallback((selected: boolean) => {
    setUserSelectedOrg(selected);
    persistToLocalStorage(STORAGE_KEYS.USER_SELECTED_ORG, selected);
  }, []);

  // Enhanced setters with navigation support and persistence
  const setCurrentOrganizationId = useCallback((id: string | null, shouldNavigate: boolean = false) => {
    updateCurrentOrganizationId(id);
    updateUserSelectedOrg(true); // Mark as user-selected when called directly
    
    if (shouldNavigate && id) {
      // Navigate to the organization detail page
      navigate(`/organizations/${id}`);
    }
  }, [navigate, updateCurrentOrganizationId, updateUserSelectedOrg]);

  const setCurrentTeamId = useCallback(async (id: string | null, shouldNavigate: boolean = false) => {
    console.log('[OrganizationContext] setCurrentTeamId called', { id, shouldNavigate, currentTeamId });

    // If switching to a different team, end previous team session
    if (currentTeamId && currentTeamId !== id) {
      console.log('[OrganizationContext] Ending previous team session', currentTeamId);
      await teamSessionService.endSession(currentTeamId);
    }

    // If selecting a new team, start session
    if (id) {
      try {
        console.log('[OrganizationContext] Starting session for team', id);
        const result = await teamSessionService.startSession(id);

        if (!result.success) {
          console.error('[OrganizationContext] Failed to start team session:', result.error);

          if (result.errorCode === 'SUBSCRIPTION_EXPIRED') {
            setSubscriptionExpiredDialogOpen(true);
          } else if (result.subscriptionInfo) {
            setSessionLimitInfo({
              currentUsers: result.subscriptionInfo.currentConcurrentUsers,
              maxUsers: result.subscriptionInfo.maxConcurrentUsers
            });
            setSessionLimitDialogOpen(true);
          } else {
            showWarning(result.error || 'Failed to start team session', 10000);
          }

          // Don't update current team ID if session failed
          return;
        }

        // Session started successfully
        console.log('[OrganizationContext] Team session started successfully', result.subscriptionInfo);

        // Clear any previous errors
        setError(null);
      } catch (error) {
        console.error('[OrganizationContext] Error starting team session:', error);
        showError('Failed to start team session. Please try again.', 10000);
        return;
      }
    }

    // Update team ID in context
    console.log('[OrganizationContext] Updating currentTeamId to', id);
    updateCurrentTeamId(id);

    if (shouldNavigate && id) {
      // Navigate to the content search page when a team is selected
      console.log('[OrganizationContext] Navigating to /content');
      navigate('/content');
    } else {
      console.log('[OrganizationContext] Not navigating', { shouldNavigate, id });
    }
  }, [currentTeamId, navigate, updateCurrentTeamId, setError]);

  // Use refs to avoid stale closure issues in useCallback
  const currentOrganizationIdRef = React.useRef(currentOrganizationId);
  const currentTeamIdRef = React.useRef(currentTeamId);
  
  React.useEffect(() => {
    currentOrganizationIdRef.current = currentOrganizationId;
    currentTeamIdRef.current = currentTeamId;
  }, [currentOrganizationId, currentTeamId]);

  // Function to sync context with current route
  const syncWithCurrentRoute = useCallback((orgId?: string, teamId?: string) => {
    // Extract route parameters from current path
    const pathMatch = location.pathname.match(/^\/organizations\/([^\/]+)(?:\/teams\/([^\/]+))?/);
    
    const routeOrgId = orgId || pathMatch?.[1] || null;
    const routeTeamId = teamId || pathMatch?.[2] || null;
    
    // Update state to match current route without triggering navigation
    if (routeOrgId && routeOrgId !== currentOrganizationIdRef.current) {
      updateCurrentOrganizationId(routeOrgId);
      updateUserSelectedOrg(true); // Mark as user-selected since they navigated to this route
    }
    
    // Only update team selection if we're on an organization/team route
    // For other routes (like /content), preserve the current team selection
    if (pathMatch) {
      // We're on an organization/team route
      if (routeTeamId) {
        // Route has a specific team - sync to that team
        if (routeTeamId !== currentTeamIdRef.current) {
          updateCurrentTeamId(routeTeamId);
        }
      }
      // If route is just /organizations/{orgId} (no team), preserve current team selection
      // Don't clear team selection when navigating to organization overview page
    }
    // If not on organization/team route, preserve current team selection
  }, [location.pathname, updateCurrentOrganizationId, updateCurrentTeamId, updateUserSelectedOrg]);

  // Helper functions for loading all paginated data
  const loadAllOrganizations = useCallback(async (): Promise<Organization[]> => {
    console.log('📊 Starting loadAllOrganizations');
    let allOrganizations: Organization[] = [];
    let page = 1;
    let hasNextPage = true;
    const pageSize = 10; // Match API default page size
    
    while (hasNextPage) {
      console.log(`OrganizationContext: Loading organizations page ${page} with pageSize ${pageSize}`);
      try {
        const response = await userService.getUserOrganizationsWithMetadata(page, pageSize);
        console.log(`OrganizationContext: Received page ${page} response:`, {
          dataLength: response.data?.length || 0,
          hasNext: response.metadata?.pagination?.hasNextPage,
          hasNextPage: response.metadata?.pagination?.hasNextPage,
          totalCount: response.metadata?.pagination?.totalCount,
          currentPage: response.metadata?.pagination?.currentPage
        });
        
        // Map API response to our Organization interface
        const pageOrganizations = response.data.map(org => {
          return {
            id: org._id || org.id || '',
            _id: org._id || org.id || '',
            name: org.name,
            description: org.description,
            createdAt: org.createdOn || org.createdAt,
            updatedAt: org.updatedOn || org.updatedAt,
            // Use role from API (backend now populates roles array correctly)
            role: org.roles && org.roles.length > 0 ? org.roles[0] : org.role
          };
        });
        
        console.log(`OrganizationContext: Mapped ${pageOrganizations.length} organizations from page ${page}`);
        allOrganizations = [...allOrganizations, ...pageOrganizations];
        
        hasNextPage = response.metadata?.pagination?.hasNextPage || false;
        console.log(`OrganizationContext: Page ${page} complete. hasNextPage: ${hasNextPage}, total so far: ${allOrganizations.length}`);
        page++;
        
        // Safety check to prevent infinite loops
        if (page > 100) {
          console.warn('OrganizationContext: Reached maximum page limit for organizations');
          break;
        }
      } catch (pageError) {
        logError(`OrganizationContext: Error loading organizations page ${page}`, pageError);
        logError(`OrganizationContext: Error loading organizations page ${page}`, pageError);
        // Return a rejected promise instead of throwing to prevent webpack overlay issues
        return Promise.reject(new Error(getErrorMessageString(pageError)));
      }
    }
    
    console.log(`✅ OrganizationContext: Loaded ${allOrganizations.length} organizations across ${page - 1} pages`);
    console.log('📊 Completed loadAllOrganizations, returning organizations:', allOrganizations.map(o => o.name));
    return allOrganizations;
  }, []);

  const loadAllTeams = useCallback(async (): Promise<Team[]> => {
    let allTeams: Team[] = [];
    let page = 1;
    let hasNextPage = true;
    const pageSize = 10; // Match API default page size
    
    while (hasNextPage) {
      console.log(`OrganizationContext: Loading teams page ${page}`);
      const response = await userService.getUserTeamsWithMetadata(page, pageSize);
      
      // Map API response to our Team interface
      const pageTeams = response.data.map(team => {
        return {
          id: team._id || team.id || '',
          name: team.name,
          description: team.description,
          organizationId: team.organizationId,
          organizationName: (team as any).organizationName,
          createdAt: team.createdAt,
          updatedAt: team.updatedAt,
          // Use role from API (backend now populates roles array correctly)
          role: (team as any).roles && (team as any).roles.length > 0 ? (team as any).roles[0] : (team as any).role
        };
      });
      
      allTeams = [...allTeams, ...pageTeams];
      
      hasNextPage = response.metadata?.pagination?.hasNextPage || false;
      page++;
      
      // Safety check to prevent infinite loops
      if (page > 100) {
        console.warn('OrganizationContext: Reached maximum page limit for teams');
        break;
      }
    }
    
    console.log(`OrganizationContext: Loaded ${allTeams.length} teams across ${page - 1} pages`);
    return allTeams;
  }, []);

  // Helper functions for subscription and member management (wrapped in useCallback)
  const getOrganizationSubscriptions = useCallback((organizationId: string) => {
    const allSubs = Object.values(subscriptions);
    const filtered = allSubs.filter(sub => sub.organizationId === organizationId);
    console.log(`🔍 getOrganizationSubscriptions(${organizationId}):`, {
      totalSubscriptions: allSubs.length,
      filteredCount: filtered.length,
      allOrgIds: [...new Set(allSubs.map(s => s.organizationId))],
      requestedOrgId: organizationId,
      filtered: filtered.map(s => ({ id: s._id, name: s.name, orgId: s.organizationId }))
    });
    return filtered;
  }, [subscriptions]);

  const getOrganizationMembers = useCallback((organizationId: string) => {
    return members[organizationId] || [];
  }, [members]);

  const getOrganizationInvitations = useCallback((organizationId: string) => {
    return organizationInvitations[organizationId] || [];
  }, [organizationInvitations]);

const getTeamSubscription = (teamId: string) => {
  // Find the subscription with this team in its teams array
  for (const sub of Object.values(subscriptions)) {
    if (sub.teams && sub.teams.some(team => team.id === teamId)) {
      return sub;
    }
  }
  return null;
};

  // Organization CRUD operations
  const createOrganization = async (data: { name: string; description?: string }): Promise<Organization> => {
    setError(null);
    try {
      const response = await organizationService.createOrganization(data);
      const newOrg: Organization = {
        id: response.id || response._id || '',
        name: response.name,
        description: response.description,
        createdAt: typeof response.createdAt === 'string' ? response.createdAt : response.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: typeof response.updatedAt === 'string' ? response.updatedAt : response.updatedAt?.toISOString() || new Date().toISOString()
      };

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });

      // Automatically create a free subscription for the new organization
      // This allows users to immediately start creating teams without manual subscription setup
      try {
        console.log(`[OrganizationContext] Auto-creating free subscription for new organization ${newOrg.id}`);

        // Use a direct API call instead of createNewSubscription to avoid the 1-second delay
        const subResponse = await subscriptionService.createSubscription({
          name: 'Free subscription',
          planId: 'free',
          organizationId: newOrg.id,
          autoRenew: true
        });

        console.log(`[OrganizationContext] Successfully auto-created free subscription for organization ${newOrg.id}`);

        // Add the subscription to state immediately
        setSubscriptions(prev => ({
          ...prev,
          [(subResponse as any)._id || (subResponse as any).id]: subResponse as any
        }));

        // Trigger a background refresh to get complete metadata (limits, usage)
        // Don't await this - let it happen in the background
        setTimeout(async () => {
          try {
            const result = await subscriptionService.getOrganizationSubscriptions(newOrg.id);
            const orgSubscriptions = result.subscriptions;
            const fetchedSubscriptionsMap: Record<string, Subscription> = {};

            orgSubscriptions.forEach(sub => {
              fetchedSubscriptionsMap[sub._id] = sub as any;
            });

            setSubscriptions(prev => {
              const otherOrgSubs = Object.entries(prev)
                .filter(([_, sub]) => sub.organizationId !== newOrg.id)
                .reduce((acc, [id, sub]) => ({ ...acc, [id]: sub }), {});

              return { ...otherOrgSubs, ...fetchedSubscriptionsMap };
            });

            console.log(`[OrganizationContext] Background refresh complete for organization ${newOrg.id} subscriptions`);
          } catch (refreshError) {
            console.warn(`[OrganizationContext] Background refresh failed:`, refreshError);
          }
        }, 500);

      } catch (subError: any) {
        // Log the error but don't fail organization creation
        console.warn(`[OrganizationContext] Failed to auto-create free subscription for organization ${newOrg.id}:`, subError);

        // Check if it's an audit error - this might be a backend issue that needs investigation
        if (subError?.message?.includes('auditInfo') || subError?.message?.includes('updatedBy')) {
          console.error(`[OrganizationContext] AUDIT ERROR: Backend subscription service requires audit context. This may be a service configuration issue.`);
        }

        // User can manually add a subscription later if auto-creation fails
        // Show a user-friendly warning in the console
        console.info(`[OrganizationContext] Users can manually add a subscription from the Subscriptions tab`);
      }

      return newOrg;
    } catch (err: any) {
      const errorMessage = getErrorMessageString(err);
      setError(errorMessage);
      logError('Error creating organization', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const updateOrganization = async (id: string, data: { name?: string; description?: string }): Promise<Organization> => {
    setError(null);
    try {
      const response = await organizationService.updateOrganization(id, data);
      const updatedOrg: Organization = {
        id: response.id || response._id || '',
        name: response.name,
        description: response.description,
        createdAt: typeof response.createdAt === 'string' ? response.createdAt : response.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: typeof response.updatedAt === 'string' ? response.updatedAt : response.updatedAt?.toISOString() || new Date().toISOString()
      };

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.detail(id) });

      return updatedOrg;
    } catch (err: any) {
      const errorMessage = getErrorMessageString(err);
      setError(errorMessage);
      logError('Error updating organization', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const deleteOrganization = async (id: string): Promise<void> => {
    setError(null);
    try {
      await organizationService.deleteOrganization(id);

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() });

      if (currentOrganizationId === id) {
        updateCurrentOrganizationId(null);
      }
    } catch (err: any) {
      const errorMessage = getErrorMessageString(err);
      setError(errorMessage);
      logError('Error deleting organization', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const assignSubscriptionToTeam = async (subscriptionId: string, teamId: string) => {
    setError(null);
    try {
      console.log(`Assigning subscription ${subscriptionId} to team ${teamId}`);

      // Make the API call to assign the subscription
      await subscriptionService.assignSubscriptionToTeam(teamId, subscriptionId);

      // Find the team to get its name for optimistic update
      const team = teams.find(t => t.id === teamId);

      // Update the state to reflect the assignment
      setSubscriptions(prevSubs => {
        // If this team already has a subscription assigned, remove it first
        const currentSubs = { ...prevSubs };

        // Remove team from any existing subscriptions
        for (const subId in currentSubs) {
          // Check if teams array exists before trying to find the team
          if (!currentSubs[subId].teams) continue;

          const teamIndex = currentSubs[subId].teams.findIndex(t => t.id === teamId);

          if (teamIndex !== -1) {
            const updatedTeams = [...currentSubs[subId].teams];
            updatedTeams.splice(teamIndex, 1);

            currentSubs[subId] = {
              ...currentSubs[subId],
              teams: updatedTeams,
              usage: {
                ...currentSubs[subId].usage,
                teamsCount: Math.max(0, currentSubs[subId].usage.teamsCount - 1)
              }
            };
          }
        }

        // Now assign the team to the new subscription
        if (currentSubs[subscriptionId] && team) {
          // Ensure teams array exists, default to empty array if not
          const existingTeams = currentSubs[subscriptionId].teams || [];

          currentSubs[subscriptionId] = {
            ...currentSubs[subscriptionId],
            teams: [
              ...existingTeams,
              { id: teamId, name: team.name }
            ],
            usage: {
              ...currentSubs[subscriptionId].usage,
              teamsCount: (currentSubs[subscriptionId].usage?.teamsCount || 0) + 1
            }
          };
        }

        return currentSubs;
      });

      console.log('Successfully assigned subscription to team');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to assign subscription to team';
      setError(errorMessage);
      logError('Error assigning subscription to team', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const unassignSubscriptionFromTeam = async (subscriptionId: string, teamId: string) => {
    setError(null);
    try {
      console.log(`Removing subscription from team ${teamId}`);

      // Make the API call to remove the subscription
      await subscriptionService.removeSubscriptionFromTeam(teamId);

      // Update the state to reflect the unassignment
      setSubscriptions(prevSubs => {
        if (!prevSubs[subscriptionId]) return prevSubs;

        // Check if teams array exists before filtering
        const existingTeams = prevSubs[subscriptionId].teams || [];
        const updatedTeams = existingTeams.filter(team => team.id !== teamId);

        return {
          ...prevSubs,
          [subscriptionId]: {
            ...prevSubs[subscriptionId],
            teams: updatedTeams,
            usage: {
              ...prevSubs[subscriptionId].usage,
              teamsCount: Math.max(0, (prevSubs[subscriptionId].usage?.teamsCount || 0) - 1)
            }
          }
        };
      });

      console.log('Successfully removed subscription from team');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to unassign subscription from team';
      setError(errorMessage);
      logError('Error unassigning subscription from team', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  // Subscription management functions
  const createNewSubscription = async (data: {
    name: string;
    planId: string;
    organizationId: string
  }): Promise<Subscription> => {
    setError(null);
    try {
      const response = await subscriptionService.createSubscription({
        name: data.name,
        planId: data.planId,
        organizationId: data.organizationId,
        autoRenew: true
      });

      // The subscriptionService.createSubscription already invalidates cache (line 285)
      // But we need to refetch to get complete data with limits and usage
      try {
        console.log(`[OrganizationContext] Created subscription ${(response as any)._id}, now refreshing organization subscriptions`);

        // Add a small delay to ensure backend has processed the subscription and cache is invalidated
        await new Promise(resolve => setTimeout(resolve, 1000));

        const result = await subscriptionService.getOrganizationSubscriptions(data.organizationId);
        const orgSubscriptions = result.subscriptions;
        const fetchedSubscriptionsMap: Record<string, Subscription> = {};

        console.log(`[OrganizationContext] Fetched ${orgSubscriptions.length} subscriptions for organization ${data.organizationId} after creation`);

        // Verify the new subscription is in the fetched list
        const newSubId = (response as any)._id || (response as any).id;
        const foundNewSub = orgSubscriptions.find(sub => sub._id === newSubId);
        if (foundNewSub) {
          console.log(`[OrganizationContext] ✓ New subscription ${newSubId} found in refreshed data with limits:`, (foundNewSub as any).limits, 'usage:', (foundNewSub as any).usage);
        } else {
          console.warn(`[OrganizationContext] ✗ New subscription ${newSubId} NOT found in refreshed data! This is the cache issue.`);
        }

        // Update subscriptions state with fresh data from API
        orgSubscriptions.forEach(sub => {
          fetchedSubscriptionsMap[sub._id] = sub as any;
        });

        // Merge with existing subscriptions from other orgs
        setSubscriptions(prev => {
          // Keep subscriptions from other organizations
          const otherOrgSubs = Object.entries(prev)
            .filter(([_, sub]) => sub.organizationId !== data.organizationId)
            .reduce((acc, [id, sub]) => ({ ...acc, [id]: sub }), {});

          // Combine with refreshed org subscriptions
          const newState = { ...otherOrgSubs, ...fetchedSubscriptionsMap };
          console.log(`[OrganizationContext] Updated subscriptions state. Total subscriptions: ${Object.keys(newState).length}, for this org: ${Object.keys(fetchedSubscriptionsMap).length}`);
          return newState;
        });

        console.log(`[OrganizationContext] Successfully refreshed subscriptions for organization ${data.organizationId} after creation`);
      } catch (refreshError) {
        console.warn('[OrganizationContext] Failed to refresh organization subscriptions after creation:', refreshError);
        // Fallback to adding just the created subscription
        setSubscriptions(prev => ({
          ...prev,
          [(response as any)._id || (response as any).id]: response as any
        }));
      }

      return response as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create subscription';
      setError(errorMessage);
      logError('Error creating subscription', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const cancelSubscription = async (id: string): Promise<Subscription> => {
    setError(null);
    try {
      const response = await subscriptionService.cancelSubscription(id);

      // Find organization ID for this subscription to refresh
      const subscription = Object.values(subscriptions).find(sub => sub._id === id);
      const orgId = subscription?.organizationId;

      // Refetch subscriptions for the organization to get updated data
      if (orgId) {
        try {
          const result = await subscriptionService.getOrganizationSubscriptions(orgId);
          const orgSubscriptions = result.subscriptions;
          const fetchedSubscriptionsMap: Record<string, Subscription> = {};

          orgSubscriptions.forEach(sub => {
            fetchedSubscriptionsMap[sub._id] = sub as any;
          });

          setSubscriptions(prev => {
            const otherOrgSubs = Object.entries(prev)
              .filter(([_, sub]) => sub.organizationId !== orgId)
              .reduce((acc, [id, sub]) => ({ ...acc, [id]: sub }), {});

            return { ...otherOrgSubs, ...fetchedSubscriptionsMap };
          });
        } catch (refreshError) {
          console.warn('Failed to refresh organization subscriptions after cancellation:', refreshError);
          // Fallback to updating just this subscription
          setSubscriptions(prev => ({
            ...prev,
            [(response as any)._id || (response as any).id]: response as any
          }));
        }
      }

      return response as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to cancel subscription';
      setError(errorMessage);
      logError('Error canceling subscription', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const updateSubscriptionAddOns = async (
    id: string,
    addOns: { extraUsers?: number; extraStorage?: number; extraTeams?: number; extraApiCalls?: number }
  ): Promise<Subscription> => {
    setError(null);
    try {
      // TODO: Fix type mismatch - service expects UpdateSubscriptionAddOnsRequest with priceId array
      // For now, we'll just update state without calling the service
      console.warn('updateSubscriptionAddOns: Type mismatch between context and service - needs refactoring');

      // Find organization ID for this subscription to refresh
      const subscription = Object.values(subscriptions).find(sub => sub._id === id);
      const orgId = subscription?.organizationId;

      // Refetch subscriptions for the organization to get updated data
      if (orgId) {
        try {
          const result = await subscriptionService.getOrganizationSubscriptions(orgId);
          const orgSubscriptions = result.subscriptions;
          const fetchedSubscriptionsMap: Record<string, Subscription> = {};

          orgSubscriptions.forEach(sub => {
            fetchedSubscriptionsMap[sub._id] = sub as any;
          });

          setSubscriptions(prev => {
            const otherOrgSubs = Object.entries(prev)
              .filter(([_, sub]) => sub.organizationId !== orgId)
              .reduce((acc, [id, sub]) => ({ ...acc, [id]: sub }), {});

            return { ...otherOrgSubs, ...fetchedSubscriptionsMap };
          });
        } catch (refreshError) {
          console.warn('Failed to refresh organization subscriptions after updating add-ons:', refreshError);
        }
      }

      return subscription as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update subscription add-ons';
      setError(errorMessage);
      logError('Error updating subscription add-ons', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const updateBillingDetails = async (
    subscriptionId: string, 
    billingDetails: {
      paymentMethod: string;
      cardNumber?: string;
      expiryDate?: string;
      cvc?: string;
      billingAddress: string;
    }
  ): Promise<Subscription> => {
    setError(null);
    try {
      const response = await paymentService.updateSubscriptionBilling(subscriptionId, billingDetails);
      
      // Update state with the response directly
      setSubscriptions(prev => ({
        ...prev,
        [(response as any)._id || (response as any).id]: response as any
      }));
      
      return response as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update billing details';
      setError(errorMessage);
      logError('Error updating billing details', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const getBillingHistory = async (subscriptionId: string): Promise<any[]> => {
    setError(null);
    try {
      return await subscriptionService.getBillingHistory(subscriptionId);
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch billing history';
      setError(errorMessage);
      logError('Error fetching billing history', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };
  
  // Advanced billing integration
  const setupStripeCustomer = async (organizationId: string, data: {
    email: string,
    name: string,
    billingAddress: string,
    taxId?: string
  }): Promise<{ customerId: string }> => {
    setError(null);
    try {
      // TODO: Implement setupStripeCustomer in paymentService
      console.warn('setupStripeCustomer not implemented yet');
      throw new Error('Not implemented');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to set up Stripe customer';
      setError(errorMessage);
      logError('Error setting up Stripe customer', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };
  
  const createStripePaymentMethod = async (organizationId: string, data: {
    customerId: string,
    paymentMethodId: string
  }): Promise<{ success: boolean }> => {
    setError(null);
    try {
      // TODO: Implement createStripePaymentMethod in paymentService
      console.warn('createStripePaymentMethod not implemented yet');
      return { success: false };
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create payment method';
      setError(errorMessage);
      logError('Error creating payment method', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };
  
  const getStripeSetupIntent = async (organizationId: string): Promise<{ clientSecret: string }> => {
    setError(null);
    try {
      // TODO: Implement getStripeSetupIntent in paymentService
      console.warn('getStripeSetupIntent not implemented yet');
      throw new Error('Not implemented');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to get setup intent';
      setError(errorMessage);
      logError('Error getting setup intent', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };
  
  const updateSubscriptionAutoRenew = async (subscriptionId: string, autoRenew: boolean): Promise<Subscription> => {
    setError(null);
    try {
      const response = await subscriptionService.updateSubscriptionAutoRenew(subscriptionId, autoRenew);

      // Find organization ID for this subscription to refresh
      const subscription = Object.values(subscriptions).find(sub => sub._id === subscriptionId);
      const orgId = subscription?.organizationId;

      // Refetch subscriptions for the organization to get updated data
      if (orgId) {
        try {
          const result = await subscriptionService.getOrganizationSubscriptions(orgId);
          const orgSubscriptions = result.subscriptions;
          const fetchedSubscriptionsMap: Record<string, Subscription> = {};

          orgSubscriptions.forEach(sub => {
            fetchedSubscriptionsMap[sub._id] = sub as any;
          });

          setSubscriptions(prev => {
            const otherOrgSubs = Object.entries(prev)
              .filter(([_, sub]) => sub.organizationId !== orgId)
              .reduce((acc, [id, sub]) => ({ ...acc, [id]: sub }), {});

            return { ...otherOrgSubs, ...fetchedSubscriptionsMap };
          });
        } catch (refreshError) {
          console.warn('Failed to refresh organization subscriptions after updating auto-renew:', refreshError);
          // Fallback to updating just this subscription
          setSubscriptions(prev => ({
            ...prev,
            [(response as any)._id || (response as any).id]: response as any
          }));
        }
      }

      return response as any;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update auto-renew setting';
      setError(errorMessage);
      logError('Error updating auto-renew setting', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };
  
  const downloadInvoice = async (invoiceId: string): Promise<{ downloadUrl: string }> => {
    setError(null);
    try {
      // TODO: Implement downloadInvoice in paymentService
      console.warn('downloadInvoice not implemented yet');
      throw new Error('Not implemented');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to download invoice';
      setError(errorMessage);
      logError('Error downloading invoice', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const addSubscription = (subscription: Subscription) => {
    setSubscriptions(prev => ({
      ...prev,
      [subscription._id]: subscription
    }));
  };
  
  // Team CRUD operations
  const createTeam = async (data: { name: string; description?: string; organizationId: string; subscriptionId?: string }): Promise<Team> => {
    setError(null);
    try {
      // Try to create the team with the API
      const response = await teamService.createTeam(data);

      // Find the organization to get its name
      const org = organizations.find(o => o.id === data.organizationId);

      const newTeam: Team = {
        id: response.id || response._id || '',
        name: response.name,
        description: response.description,
        organizationId: response.organizationId,
        organizationName: org?.name,
        createdAt: typeof response.createdAt === 'string' ? response.createdAt : response.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: typeof response.updatedAt === 'string' ? response.updatedAt : response.updatedAt?.toISOString() || new Date().toISOString()
      };

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
      queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(data.organizationId) });

      return newTeam;
    } catch (err: any) {
      // Handle specific errors related to subscription limits
      if (err.code === 'TEAM_LIMIT_REACHED' || 
          (err.message && err.message.includes('subscription')) || 
          (err.message && err.message.includes('maximum number of teams'))) {
        setError('You have reached the maximum number of teams allowed for your subscription plan. Please upgrade your plan to create more teams.');
      } else {
        const errorMessage = err.message || 'Failed to create team';
        setError(errorMessage);
      }
      throw err; // Rethrow the error to be handled by the caller
    }
  };

  const updateTeam = async (id: string, data: { name?: string; description?: string }): Promise<Team> => {
    setError(null);
    try {
      const response = await teamService.updateTeam(id, data);
      
      // Find the organization to get its name
      const org = organizations.find(o => o.id === response.organizationId);
      
      const updatedTeam: Team = {
        id: response.id || response._id || '',
        name: response.name,
        description: response.description,
        organizationId: response.organizationId,
        organizationName: org?.name,
        createdAt: typeof response.createdAt === 'string' ? response.createdAt : response.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: typeof response.updatedAt === 'string' ? response.updatedAt : response.updatedAt?.toISOString() || new Date().toISOString()
      };

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.detail(id) });
      if (response.organizationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(response.organizationId) });
      }

      return updatedTeam;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update team';
      setError(errorMessage);
      logError('Error updating team', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const deleteTeam = async (id: string): Promise<void> => {
    setError(null);
    try {
      // Find the team to get its organizationId before deleting
      const teamToDelete = teams.find(t => t.id === id);

      await teamService.deleteTeam(id);

      // Invalidate React Query cache to trigger refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() });
      if (teamToDelete?.organizationId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.organizations.teams(teamToDelete.organizationId) });
      }

      if (currentTeamId === id) {
        updateCurrentTeamId(null);
      }
      
      // Remove team from any subscriptions
      setSubscriptions(prevSubs => {
        const updatedSubs = { ...prevSubs };
        for (const subId in updatedSubs) {
          // Check if team is associated with this subscription
          if (updatedSubs[subId].teams.some(t => t.id === id)) {
            updatedSubs[subId] = {
              ...updatedSubs[subId],
              teams: updatedSubs[subId].teams.filter(t => t.id !== id),
              usage: {
                ...updatedSubs[subId].usage,
                teamsCount: Math.max(0, updatedSubs[subId].usage.teamsCount - 1)
              }
            };
          }
        }
        return updatedSubs;
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to delete team';
      setError(errorMessage);
      logError('Error deleting team', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  // Membership management functions
  const inviteMember = async (organizationId: string, data: { email: string; role: string }): Promise<any> => {
    setError(null);
    try {
      const response = await invitationService.createInvitation(organizationId, { email: data.email, role: data.role });
      
      // Refresh members and invitations for this organization
      await fetchOrganizationMembers(organizationId);
      await fetchOrganizationInvitations(organizationId);
      
      return response;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to invite member';
      setError(errorMessage);
      logError('Error inviting member', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  // Invalidate the React Query caches that back the organization/team selector
  // so it refetches after a membership change (accepting an invitation, or
  // leaving an org/team). refreshData() only reads the React Query cache; it
  // does not invalidate it, so without this the selector stays stale.
  const refreshMemberships = useCallback(async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.users.organizations() }),
      queryClient.invalidateQueries({ queryKey: queryKeys.users.teams() })
    ]);
  }, [queryClient]);

  const acceptInvitation = async (invitationId: string): Promise<void> => {
    setError(null);
    try {
      await invitationService.acceptInvitationById(invitationId);

      // Update invitations state - mark as accepted rather than removing
      setInvitations(prev => prev.map(inv =>
        inv.id === invitationId
          ? { ...inv, status: 'accepted' }
          : inv
      ));

      // Update organization invitations state
      setOrganizationInvitations(prev => {
        const updated = { ...prev };
        for (const orgId in updated) {
          updated[orgId] = updated[orgId].map(inv =>
            inv.id === invitationId
              ? { ...inv, status: 'accepted' }
              : inv
          );
        }
        return updated;
      });

      // Accepting an invitation adds an org/team membership — invalidate the
      // selector's React Query caches so the new org/team appears, then refresh
      // the rest of the context state (members, subscriptions, invitations).
      await refreshMemberships();
      await refreshData();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to accept invitation';
      setError(errorMessage);
      logError('Error accepting invitation', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const declineInvitation = async (invitationId: string): Promise<void> => {
    setError(null);
    try {
      await invitationService.declineInvitationById(invitationId);

      // Update invitations state - mark as declined rather than removing
      setInvitations(prev => prev.map(inv =>
        inv.id === invitationId
          ? { ...inv, status: 'declined' }
          : inv
      ));

      // Update organization invitations state
      setOrganizationInvitations(prev => {
        const updated = { ...prev };
        for (const orgId in updated) {
          updated[orgId] = updated[orgId].map(inv =>
            inv.id === invitationId
              ? { ...inv, status: 'declined' }
              : inv
          );
        }
        return updated;
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to decline invitation';
      setError(errorMessage);
      logError('Error declining invitation', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const cancelInvitation = async (invitationId: string): Promise<void> => {
    setError(null);
    try {
      await invitationService.cancelInvitation(invitationId);

      // Update invitations state - mark as canceled rather than removing
      setInvitations(prev => prev.map(inv =>
        inv.id === invitationId
          ? { ...inv, status: 'canceled' }
          : inv
      ));

      // Update organization invitations state
      setOrganizationInvitations(prev => {
        const updated = { ...prev };
        for (const orgId in updated) {
          updated[orgId] = updated[orgId].map(inv =>
            inv.id === invitationId
              ? { ...inv, status: 'canceled' }
              : inv
          );
        }
        return updated;
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to cancel invitation';
      setError(errorMessage);
      logError('Error canceling invitation', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const removeMember = async (organizationId: string, userId: string): Promise<void> => {
    setError(null);
    try {
      await invitationService.cancelInvitation(userId); // Using cancelInvitation as member removal
      
      // Update members state
      setMembers(prev => {
        if (!prev[organizationId]) return prev;
        
        return {
          ...prev,
          [organizationId]: prev[organizationId].filter(member => member.userId !== userId)
        };
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to remove member';
      setError(errorMessage);
      logError('Error removing member', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  const updateMemberRole = async (organizationId: string, userId: string, role: string): Promise<Member> => {
    setError(null);
    try {
      // TODO: Need organization member role update API method
      await teamService.updateTeamMemberRole(organizationId, userId, role);
      
      // Since the API doesn't return the updated member, we'll find it in state and update it
      const currentMembers = members[organizationId] || [];
      const existingMember = currentMembers.find(m => m.userId === userId);
      
      const updatedMember: Member = {
        id: existingMember?.id || userId,
        userId: userId,
        name: existingMember?.name || 'Unknown',
        email: existingMember?.email || '',
        role: role,
        avatarUrl: existingMember?.avatarUrl,
        status: existingMember?.status || 'active',
        createdAt: existingMember?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Update members state
      setMembers(prev => {
        if (!prev[organizationId]) return prev;
        
        return {
          ...prev,
          [organizationId]: prev[organizationId].map(member => 
            member.userId === userId ? updatedMember : member
          )
        };
      });
      
      return updatedMember;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to update member role';
      setError(errorMessage);
      logError('Error updating member role', err);
      // Return a rejected promise instead of throwing to prevent webpack overlay issues
      return Promise.reject(new Error(errorMessage));
    }
  };

  // Helper function to fetch organization members
  const fetchOrganizationMembers = useCallback(async (organizationId: string) => {
    try {
      const membersList = await organizationService.listOrganizationMembers(organizationId);
      
      // Map API response to our Member interface
      const mappedMembers = membersList.map(member => ({
        id: (member as any)._id || (member as any).id,
        userId: (member as any).user_id || (member as any).userId,
        name: (member as any).user_name || (member as any).name,
        email: (member as any).user_email || (member as any).email,
        role: (member as any).role_name || (member as any).role,
        avatarUrl: (member as any).avatarUrl,
        status: (member as any).status || 'active',
        createdAt: (member as any).createdAt || new Date().toISOString(),
        updatedAt: (member as any).updatedAt || new Date().toISOString()
       }));
 
      // Log the mapped members structure clearly
      console.log(`OrganizationContext: Mapped members for org ${organizationId}:`, JSON.stringify(mappedMembers, null, 2));
       setMembers(prev => ({
         ...prev,
        [organizationId]: mappedMembers
      }));
      
      return mappedMembers;
    } catch (error) {
      console.error(`Failed to fetch members for organization ${organizationId}:`, error);
      return [];
    }
  }, []); // No external dependencies needed here, ApiService is stable

  // Helper function to fetch organization invitations
  const fetchOrganizationInvitations = useCallback(async (organizationId: string) => {
    try {
      const invitationsList = await invitationService.listInvitations(organizationId);
      
      // Map API response to our Invitation interface
      const mappedInvitations = ((invitationsList as any).data || invitationsList).map((item, index) => {
        // The API returns { invitation: {...}, organizationName: ..., ... }
        const invitationData = item.invitation || item;
        
        if (!invitationData.id) {
          console.error('Invitation missing ID at index', index, 'in organization', organizationId, invitationData);
          throw new Error(`Invalid invitation data: missing ID at index ${index}`);
        }
        
        return {
          id: invitationData.id,
          email: invitationData.inviteeEmail || 'N/A',
          userId: invitationData.inviteeUserId,
          scope: invitationData.organizationId ? 'organization' : 'team',
          contextId: invitationData.organizationId || invitationData.teamId,
          organizationId: invitationData.organizationId,
          roleId: invitationData.roleId,
          invitedBy: invitationData.inviterUserId,
          status: invitationData.status || 'pending',
          expiresAt: invitationData.expiresAt,
          token: invitationData.token,
          createdAt: invitationData.createdAt,
          updatedAt: invitationData.updatedAt,
          organizationName: item.organizationName,
          teamName: item.teamName,
          roleName: invitationData.roleName
        };
      });
      
      setOrganizationInvitations(prev => ({
        ...prev,
        [organizationId]: mappedInvitations
      }));
      
       return mappedInvitations;
     } catch (error) {
       console.error(`Failed to fetch invitations for organization ${organizationId}:`, error);
       throw error; // Re-throw the error
     }
   }, []); // No external dependencies needed here, ApiService is stable
  
  // Fetch all invitations for the current user
  const fetchUserInvitations = useCallback(async () => {
    try {
      console.log('OrganizationContext: Fetching user invitations');
      const response = await userService.getUserInvitations();
      console.log('OrganizationContext: User invitations fetched successfully', response);
      
      // Ensure we have an array to work with (handle API Response format)
      const invitationsList = Array.isArray(response) ? response : (response?.data || []);
      
      // Helper function to map status to readable text - handles both numeric and string formats
      const mapStatus = (status: string | number) => {
        const statusStr = String(status).toLowerCase();
        switch (statusStr) {
          case '0':
          case 'pending': 
            return 'pending';
          case '1':
          case 'accepted': 
            return 'accepted';
          case '2':
          case 'declined': 
            return 'declined';
          case '3':
          case 'expired': 
            return 'expired';
          case '4':
          case 'canceled':
          case 'cancelled': 
            return 'canceled';
          default: 
            return 'pending';
        }
      };

      // Map API response to our Invitation interface
      const mappedInvitations = invitationsList.map((item, index) => {
        // Extract the actual invitation from the nested structure
        const invitation = item.invitation || item;
        
        // Determine if this is a team invitation based on available data
        const hasTeamId = invitation.teamId || invitation.team_id || item.teamId;
        const hasOrgId = invitation.organizationId || invitation.organization_id || item.organizationId;
        const contextType = invitation.context?.type;
        
        // Determine scope: if we have a team ID or context type is team, it's a team invitation
        const scope = hasTeamId || contextType === 'team' ? 'team' : 'organization';
        
        if (!invitation.id) {
          console.error('User invitation missing ID at index', index, invitation);
          throw new Error(`Invalid user invitation data: missing ID at index ${index}`);
        }
        
        const mappedInvitation = {
          id: invitation.id,
          email: invitation.invitee_email || item.inviteeEmail || 'N/A',
          userId: invitation.invitee_user_id || invitation.userId,
          scope: scope,
          contextId: scope === 'team' ? (hasTeamId || invitation.context?.entity_id) : (hasOrgId || invitation.context?.entity_id),
          organizationId: hasOrgId || (scope === 'organization' ? invitation.context?.entity_id : null),
          roleId: invitation.role_id || invitation.roleId,
          invitedBy: invitation.inviter_user_id || invitation.invitedBy,
          status: mapStatus(invitation.status),
          expiresAt: invitation.expires_at || invitation.expiresAt,
          token: invitation.token,
          createdAt: invitation.created_at || invitation.createdAt,
          updatedAt: invitation.updated_at || invitation.updatedAt,
          organizationName: item.organizationName || invitation.organization_name || invitation.organizationName,
          teamName: item.teamName || invitation.team_name || invitation.teamName,
          roleName: item.proposedRoles?.[0] || invitation.proposed_roles?.[0] || invitation.role_name || invitation.roleName
        };

        // Debug logging for first few invitations
        if (index < 3) {
          console.log(`OrganizationContext: Mapping invitation ${index}:`, {
            originalItem: item,
            extractedInvitation: invitation,
            hasTeamId,
            hasOrgId,
            contextType,
            determinedScope: scope,
            rawStatus: invitation.status,
            mappedStatus: mappedInvitation.status,
            final: mappedInvitation
          });
        }

        return mappedInvitation;
      });
      
      setInvitations(mappedInvitations as any);
      
      return mappedInvitations;
    } catch (error: any) {
      // Only log the error if it's not a circuit breaker or auth issue
      if (error.isCircuitBreaker) {
        console.warn('OrganizationContext: Circuit breaker error when fetching invitations, using empty array');
      } else if (error.status === 401 || error.code === 'UNAUTHORIZED') {
        console.warn('OrganizationContext: Authentication error when fetching invitations');
       } else {
         console.error('OrganizationContext: Failed to fetch user invitations:', error);
       }
       // Do not re-throw; let the caller handle the lack of data or error state
       return []; // Return empty array to signify failure gracefully
     }
   }, []); // No external dependencies needed here, ApiService is stable

  // Tracks if we're currently fetching invitations to prevent duplicate requests
  const [isFetchingInvitations, setIsFetchingInvitations] = useState(false);

  const refreshInvitations = useCallback(async () => {
    if (!isAuthenticated || !user || isFetchingInvitations) {
      // Skip if not authenticated, no user, or already fetching
      if (!isAuthenticated || !user) {
        setInvitations([]);
        setOrganizationInvitations({});
      }
      return;
    }
    
    try {
      setIsFetchingInvitations(true);
      
      // Fetch user's invitations
      await fetchUserInvitations();
      
      // Fetch invitations for each organization
      for (const org of organizations) {
        await fetchOrganizationInvitations(org.id);
       }
     } catch (error) {
       console.error('Failed to refresh invitations:', error);
       // Do not re-throw; the error is logged, and refreshData's catch will handle overall state
     } finally {
       setIsFetchingInvitations(false);
    }
   // Add dependencies for useCallback - Only include stable external dependencies
   }, [isAuthenticated, user, fetchUserInvitations, fetchOrganizationInvitations]); 
 
   const refreshData = useCallback(async () => {
    if (refreshDataInProgressRef.current) {
      console.log('⚠️ OrganizationContext: refreshData already in progress, skipping');
      return;
    }
    
    console.log('🔄 OrganizationContext: refreshData called');
    refreshDataInProgressRef.current = true;
    
    const token = localStorage.getItem('accessToken');
    
    if (!isAuthenticated || !user || !token) {
      // Add more details to help debug the exact state that's failing
      console.log('OrganizationContext: Auth state check - ', {
        isAuthenticated,
        hasUser: !!user,
        hasToken: !!token,
        userId: user?._id
      });
      
      // Only log and clear data if we're not in the middle of the login process
      // Check if token exists but user data is still loading
      if (!token || !isLoading) {
        console.log('OrganizationContext: Not authenticated or no user, clearing data');
        // Organizations and teams are handled by React Query (will auto-clear on auth change)
        setMembers({});
        setInvitations([]);
        setOrganizationInvitations({});
        setSubscriptions({});
        updateCurrentOrganizationId(null);
        updateCurrentTeamId(null);
        setCurrentSubscriptionId(null);
        setAdditionalDataLoading(false);
        setError(null);
      } else {
        console.log('OrganizationContext: Auth in progress (token exists but user not loaded yet), waiting...');
      }
      refreshDataInProgressRef.current = false;
      return;
    }

    console.log('🚀 OrganizationContext: Refreshing data for authenticated user', user._id, new Date().toISOString());
    setAdditionalDataLoading(true);
    setError(null);

    try {
      // React Query is already fetching organizations and teams!
      // Just use the data from the hooks (automatic deduplication)
      const fetchedOrgs = directOrganizations;
      const fetchedTeams = teams;
      let fetchedMembersMap: Record<string, Member[]> = {};
      let allOrgs: Organization[] = []; // Declare in outer scope for use in subscription fetching

      try {
        // Organizations and teams are already loaded by React Query
        console.log('OrganizationContext: Using React Query data', {
          organizations: fetchedOrgs.length,
          teams: fetchedTeams.length
        });

        // Add implicit organizations from teams (organizations where user is only a team member)
        const existingOrgIds = new Set(fetchedOrgs.map(org => org.id));
        const newImplicitOrgs: Organization[] = [];

        for (const team of fetchedTeams) {
          if (team.organizationId && !existingOrgIds.has(team.organizationId)) {
            // Check if we already added this implicit org
            if (!newImplicitOrgs.some(org => org.id === team.organizationId)) {
              // Fetch organization details for implicit membership
              try {
                console.log(`OrganizationContext: Fetching implicit organization ${team.organizationId} for team ${team.name}`);
                const orgResponse = await organizationService.getOrganization(team.organizationId);
                if (orgResponse.data) {
                  const implicitOrg: Organization = {
                    id: orgResponse.data._id || orgResponse.data.id || team.organizationId,
                    _id: orgResponse.data._id || orgResponse.data.id,
                    name: orgResponse.data.name || team.organizationName || 'Unknown Organization',
                    description: orgResponse.data.description,
                    createdAt: (orgResponse.data as any).createdOn || orgResponse.data.createdAt || new Date().toISOString(),
                    updatedAt: (orgResponse.data as any).updatedOn || orgResponse.data.updatedAt || new Date().toISOString(),
                    // Add a flag to indicate implicit membership (for UI purposes)
                    isImplicit: true
                  } as Organization & { isImplicit: boolean };
                  newImplicitOrgs.push(implicitOrg);
                  existingOrgIds.add(team.organizationId);
                }
              } catch (orgError) {
                console.warn(`Failed to fetch implicit organization ${team.organizationId}:`, orgError);
                // Create a minimal organization entry
                const minimalOrg: Organization = {
                  id: team.organizationId,
                  name: team.organizationName || 'Organization',
                  createdAt: '',
                  updatedAt: '',
                  isImplicit: true
                } as Organization & { isImplicit: boolean };
                newImplicitOrgs.push(minimalOrg);
                existingOrgIds.add(team.organizationId);
              }
            }
          }
        }

        if (newImplicitOrgs.length > 0) {
          console.log(`OrganizationContext: Setting ${newImplicitOrgs.length} implicit organizations from team memberships`);
          setImplicitOrganizations(newImplicitOrgs);
        } else {
          console.log(`OrganizationContext: No implicit organizations found, clearing state`);
          setImplicitOrganizations([]);
        }

        // Combine direct and implicit orgs for member fetching
        allOrgs = [...fetchedOrgs, ...newImplicitOrgs];

        // Fetch members for all organizations (both direct and implicit)
        for (const org of allOrgs) {
          try {
            console.log(`OrganizationContext: Fetching members for organization ${org.id}`);
            const members = await fetchOrganizationMembers(org.id);
            fetchedMembersMap[org.id] = members;
          } catch (memberError: any) {
            if (memberError.isCircuitBreaker) {
              console.warn(`OrganizationContext: Circuit breaker error fetching members for org ${org.id}`);
            } else if (memberError.status === 401 || memberError.code === 'UNAUTHORIZED') {
              console.warn(`OrganizationContext: Authentication error fetching members for org ${org.id}`);
            } else {
              console.error(`OrganizationContext: Failed to fetch members for organization ${org.id}:`, memberError);
            }
          }
        }
        
        // Update only members state (orgs/teams come from React Query)
        console.log('OrganizationContext: Setting members data', {
          organizations: fetchedOrgs.length,
          teams: fetchedTeams.length,
          members: Object.keys(fetchedMembersMap).length
        });

        setMembers(fetchedMembersMap);
       } catch (error: any) {
         // Handle specific error types to prevent logouts
        if (error.isCircuitBreaker) {
          console.warn('OrganizationContext: Circuit breaker error fetching members');
        } else if (error.status === 401 || error.code === 'UNAUTHORIZED') {
          console.warn('OrganizationContext: Authentication error fetching members');
        } else {
          console.error('OrganizationContext: Failed to fetch members:', error);
        }

        // Clear members on error (orgs/teams handled by React Query)
        setMembers({});
      }
      
      // Fetch subscriptions for each organization
      try {
        const fetchedSubscriptionsMap: Record<string, Subscription> = {};

        // Fetch subscriptions for each organization the user belongs to (both direct and implicit)
        console.log(`🔍 OrganizationContext.refreshData: Fetching subscriptions for ${allOrgs.length} organizations`);
        for (const org of allOrgs) {
          try {
            const result = await subscriptionService.getOrganizationSubscriptions(org.id);
            const orgSubscriptions = result.subscriptions;
            console.log(`🔍 OrganizationContext.refreshData: Org "${org.name}" (${org.id}) has ${orgSubscriptions.length} subscriptions`);
            // Add each subscription to the map using subscription ID as key
            orgSubscriptions.forEach(sub => {
              console.log(`🔍   - Adding subscription ${sub._id} (${sub.name}) with organizationId: ${sub.organizationId}`);
              fetchedSubscriptionsMap[sub._id] = sub as any;
            });
          } catch (error) {
            console.warn(`Failed to fetch subscriptions for organization ${org.id}:`, error);
            // Continue with other organizations even if one fails
          }
        }

        console.log(`🔍 OrganizationContext.refreshData: Setting ${Object.keys(fetchedSubscriptionsMap).length} total subscriptions in state`);
        setSubscriptions(fetchedSubscriptionsMap);
      } catch (error) {
        console.error('Failed to fetch organization subscriptions:', error);
        setSubscriptions({});
      }
      
      // Use combined organizations (direct + implicit) and fetched teams
      const orgsToUse = organizations; // Use the computed organizations array
      const teamsToUse = fetchedTeams;

      // Validate persisted organization selection
      const persistedOrgIsValid = currentOrganizationId && orgsToUse.some(o => o.id === currentOrganizationId);
      const persistedTeamIsValid = currentTeamId && teamsToUse.some(t => t.id === currentTeamId);
      
      // Clear invalid selections and log for debugging
      if (currentOrganizationId && !persistedOrgIsValid) {
        console.log(`OrganizationContext: Clearing invalid persisted organization ${currentOrganizationId}`);
        updateCurrentOrganizationId(null);
        updateUserSelectedOrg(false); // Reset user selection flag since their selection is no longer valid
      }
      
      if (currentTeamId && !persistedTeamIsValid) {
        console.log(`OrganizationContext: Clearing invalid persisted team ${currentTeamId}`);
        updateCurrentTeamId(null);
      }
      
      // If team is valid but belongs to a different org than currently selected, update org
      if (persistedTeamIsValid && currentTeamId) {
        const team = teamsToUse.find(t => t.id === currentTeamId);
        if (team && team.organizationId !== currentOrganizationId) {
          console.log(`OrganizationContext: Updating organization to match persisted team ${currentTeamId}`);
          updateCurrentOrganizationId(team.organizationId);
          updateUserSelectedOrg(true); // Mark as user-selected since team selection implies org preference
        }
      }
      
      // Set default organization selection only if none exists and no user selection has been made
      const finalOrgId = currentOrganizationId; // Get updated value after validation
      if (!userSelectedOrg && !finalOrgId && orgsToUse.length > 0) {
        console.log('OrganizationContext: Auto-selecting first available organization');
        updateCurrentOrganizationId(orgsToUse[0].id);
      }

      // Set default subscription selection if none exists
      if (currentOrganizationId && (!currentSubscriptionId || !Object.values(subscriptions).some(s => s._id === currentSubscriptionId))) {
        const orgSubs = Object.values(subscriptions).filter(sub => sub.organizationId === currentOrganizationId);
        if (orgSubs.length > 0) {
          setCurrentSubscriptionId(orgSubs[0]._id);
        }
      }
      
      // Fetch invitations
      await refreshInvitations();
    } catch (err: any) {
      console.error('Failed to fetch additional organization data:', err);
      // Use the utility function to get a user-friendly message
      setError(getUserFriendlyErrorMessage(err));
    } finally {
      setAdditionalDataLoading(false);
      refreshDataInProgressRef.current = false;
    }
  // Add dependencies for useCallback
   // Only include essential dependencies that should trigger re-creation of refreshData
   // Note: organizations and teams come from React Query hooks
   }, [isAuthenticated, user, refreshInvitations, updateCurrentOrganizationId, updateCurrentTeamId, updateUserSelectedOrg, organizations, teams]); 
 
   // Sync with route changes
  useEffect(() => {
    syncWithCurrentRoute();
  }, [location.pathname, syncWithCurrentRoute]);

  // Clear team selection when switching to organization with no teams
  useEffect(() => {
    if (currentOrganizationId && teams.length > 0) {
      const orgTeams = teams.filter(team => team.organizationId === currentOrganizationId);
      const currentTeamBelongsToOrg = currentTeamId && orgTeams.some(team => team.id === currentTeamId);
      
      // Only clear team selection if current team doesn't belong to the selected org
      // Do NOT auto-select teams - let user choose or route handle it
      if (currentTeamId && !currentTeamBelongsToOrg) {
        updateCurrentTeamId(null);
      }
    }
  }, [currentOrganizationId, teams, currentTeamId, updateCurrentTeamId]);

  // Update organization when team is selected (but don't auto-select teams)
  useEffect(() => {
    if (currentTeamId && teams.length > 0) {
      const team = teams.find(team => team.id === currentTeamId);
      if (team && team.organizationId !== currentOrganizationId) {
        updateCurrentOrganizationId(team.organizationId);
      }
    }
  }, [currentTeamId, teams, currentOrganizationId, updateCurrentOrganizationId]); // Added currentOrganizationId back since updateCurrentOrganizationId prevents loops

  // Update current subscription when organization changes
  useEffect(() => {
    if (currentOrganizationId) {
      const orgSubs = Object.values(subscriptions).filter(sub => sub.organizationId === currentOrganizationId);
      if (orgSubs.length > 0 && (!currentSubscriptionId || !orgSubs.some(sub => sub._id === currentSubscriptionId))) {
        setCurrentSubscriptionId(orgSubs[0]._id);
      }
    }
  }, [currentOrganizationId, subscriptions, currentSubscriptionId]);

   // Fetch data on initial load and when auth state changes
   useEffect(() => {
     refreshData();
   }, [isAuthenticated]);

   // Fetch implicit organizations when teams are loaded
   useEffect(() => {
     const fetchImplicitOrgs = async () => {
       if (!isAuthenticated || teamsLoading || teams.length === 0) {
         return;
       }

       const existingOrgIds = new Set(directOrganizations.map(org => org.id));
       const newImplicitOrgs: Organization[] = [];

       for (const team of teams) {
         if (team.organizationId && !existingOrgIds.has(team.organizationId)) {
           // Check if we already added this implicit org
           if (!newImplicitOrgs.some(org => org.id === team.organizationId)) {
             try {
               const orgResponse = await organizationService.getOrganization(team.organizationId);
               if (orgResponse.data) {
                 const implicitOrg: Organization = {
                   id: orgResponse.data._id || orgResponse.data.id || team.organizationId,
                   _id: orgResponse.data._id || orgResponse.data.id,
                   name: orgResponse.data.name || team.organizationName || 'Unknown Organization',
                   description: orgResponse.data.description,
                   createdAt: (orgResponse.data as any).createdOn || orgResponse.data.createdAt || new Date().toISOString(),
                   updatedAt: (orgResponse.data as any).updatedOn || orgResponse.data.updatedAt || new Date().toISOString(),
                   isImplicit: true
                 } as Organization & { isImplicit: boolean };
                 newImplicitOrgs.push(implicitOrg);
                 existingOrgIds.add(team.organizationId);
               }
             } catch (orgError) {
               console.warn(`Failed to fetch implicit organization ${team.organizationId}:`, orgError);
               // Create a minimal organization entry
               const minimalOrg: Organization = {
                 id: team.organizationId,
                 name: team.organizationName || 'Organization',
                 createdAt: '',
                 updatedAt: '',
                 isImplicit: true
               } as Organization & { isImplicit: boolean };
               newImplicitOrgs.push(minimalOrg);
               existingOrgIds.add(team.organizationId);
             }
           }
         }
       }

       if (newImplicitOrgs.length > 0) {
         console.log(`OrganizationContext: Added ${newImplicitOrgs.length} implicit organizations`);
         setImplicitOrganizations(newImplicitOrgs);
       } else {
         setImplicitOrganizations([]);
       }
     };

     fetchImplicitOrgs();
   }, [isAuthenticated, teams, directOrganizations, teamsLoading]);

   // Separately fetch subscriptions when organizations load from React Query
   useEffect(() => {
     const fetchSubscriptionsForOrgs = async () => {
       if (!isAuthenticated || organizations.length === 0 || orgsLoading) {
         console.log('🔍 OrganizationContext: Skipping subscription fetch', { isAuthenticated, orgCount: organizations.length, orgsLoading });
         return;
       }

       console.log(`🔍 OrganizationContext: Organizations loaded (${organizations.length}), fetching subscriptions`);
       const fetchedSubscriptionsMap: Record<string, Subscription> = {};

       for (const org of organizations) {
         try {
           const result = await subscriptionService.getOrganizationSubscriptions(org.id);
           const orgSubscriptions = result.subscriptions;
           console.log(`🔍 OrganizationContext: Org "${org.name}" (${org.id}) has ${orgSubscriptions.length} subscriptions`);
           orgSubscriptions.forEach(sub => {
             console.log(`🔍   - Adding subscription ${sub._id} (${sub.name}) with organizationId: ${sub.organizationId}`);
             fetchedSubscriptionsMap[sub._id] = sub as any;
           });
         } catch (error) {
           console.warn(`Failed to fetch subscriptions for organization ${org.id}:`, error);
         }
       }

       console.log(`🔍 OrganizationContext: Setting ${Object.keys(fetchedSubscriptionsMap).length} total subscriptions in state`);
       setSubscriptions(fetchedSubscriptionsMap);
     };

     fetchSubscriptionsForOrgs();
   }, [isAuthenticated, organizations.length, orgsLoading, implicitOrganizations.length]);
 
   return (
    <OrganizationContext.Provider
      value={{
        organizations,
        teams,
        members,
        invitations,
        organizationInvitations,
        subscriptions,
        currentOrganizationId,
        currentTeamId,
        currentSubscriptionId,
        setCurrentOrganizationId,
        setCurrentTeamId,
        setCurrentSubscriptionId,
        syncWithCurrentRoute,
        getOrganizationSubscriptions,
        getOrganizationMembers,
        getOrganizationInvitations,
        assignSubscriptionToTeam,
        unassignSubscriptionFromTeam,
        getTeamSubscription,
        addSubscription,
        createNewSubscription,
        cancelSubscription,
        updateSubscriptionAddOns,
        updateBillingDetails,
        getBillingHistory,
        setupStripeCustomer,
        createStripePaymentMethod,
        getStripeSetupIntent,
        updateSubscriptionAutoRenew,
        downloadInvoice,
        createOrganization,
        updateOrganization,
        deleteOrganization,
        createTeam,
        updateTeam,
        deleteTeam,
        inviteMember,
        acceptInvitation,
        declineInvitation,
        cancelInvitation,
        removeMember,
        updateMemberRole,
        refreshData,
        refreshInvitations,
        refreshMemberships,
        isLoading,
        error
      }}
    >
      {children}
      <TeamSessionLimitDialog
        open={sessionLimitDialogOpen}
        currentUsers={sessionLimitInfo.currentUsers}
        maxUsers={sessionLimitInfo.maxUsers}
        onClose={() => setSessionLimitDialogOpen(false)}
      />
      <SubscriptionExpiredDialog
        open={subscriptionExpiredDialogOpen}
        onClose={() => setSubscriptionExpiredDialogOpen(false)}
      />
    </OrganizationContext.Provider>
  );
};
